import { getFragmentData } from '@api/gql';
import { useInventoryResource } from '../services/use-inventory-resource';
import { AppDrawer, AppDrawerHeader, EmptyState, Loader } from '@components';
import { Stack, Tab, Tabs } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DETAILED_RESOURCE_NODE_FIELDS_FRAGMENT, TAGS_FIELD_FRAGMENT } from '@Inventory/services/gql';
import { InventoryPageQueryKeys } from '@Inventory/common/queryKeys';
import { ResourceDetailsTab } from '@Inventory/components/ResourceDetailsTab';
import { EntitlementsTab } from '@Inventory/organisms/EntitlementsTab';
import { getEntitlementsCount } from '@Inventory/common/utils';

export function InventoryDrawer() {
  const [queryParams, setQueryParams] = useSearchParams();
  const resourceId = queryParams.get(InventoryPageQueryKeys.ResourceId);
  const query = queryParams.get(InventoryPageQueryKeys.Query) || '';

  const closeDrawer = () => {
    const newQueryParam = new URLSearchParams(queryParams);
    newQueryParam.delete(InventoryPageQueryKeys.ResourceId);
    setQueryParams(newQueryParam);
  };

  return (
    <AppDrawer
      open={!!resourceId}
      onClose={closeDrawer}
      width={800}
      header={<AppDrawerHeader title="Entitlements Details" onCloseClick={closeDrawer} />}
    >
      {resourceId && <InventoryDrawerContent resourceId={resourceId} query={query} />}
    </AppDrawer>
  );
}

enum InventoryDrawerTabs {
  Entitlements,
  ResourceDetails,
}

function InventoryDrawerContent({ resourceId, query }: { resourceId: string; query: string }) {
  const [activeTab, setActiveTab] = useState(InventoryDrawerTabs.Entitlements);

  const { data, isFetched, error } = useInventoryResource(resourceId, { query });

  if (!isFetched) {
    return <Loader />;
  }

  const resourceData = getFragmentData(
    DETAILED_RESOURCE_NODE_FIELDS_FRAGMENT,
    data?.inventoryResources.edges[0].node.resource,
  );
  const tagsData = getFragmentData(TAGS_FIELD_FRAGMENT, resourceData?.tags);
  const permissionsCount = data?.inventoryResources.edges[0].node.permissionsCount;
  const entitlementsCount = data?.inventoryResources.edges[0].node.entitlementsCount;

  const displayEntitlementsCount =
    permissionsCount && entitlementsCount ? getEntitlementsCount(entitlementsCount, permissionsCount) : undefined;

  if (error || !resourceData || !tagsData) {
    return <EmptyState title="No Data Found" imgSrc="/static/EmptyStateImages/not-found-illustration.svg" />;
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => setActiveTab(newValue);

  return (
    <Stack direction="column" spacing={2}>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label="Entitlements" />
        <Tab label="Resource Details" />
      </Tabs>
      {activeTab === InventoryDrawerTabs.ResourceDetails && (
        <ResourceDetailsTab resource={resourceData} tagsData={[...tagsData]} />
      )}
      {activeTab === InventoryDrawerTabs.Entitlements && (
        <EntitlementsTab resource={resourceData} query={query} entitlementsCount={displayEntitlementsCount} />
      )}
    </Stack>
  );
}
